import { useState } from "react";
import { useClerk } from "@clerk/react";
import { Download, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { errMsg, useApi } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { useMe } from "@/context/MeContext";
import { useLogout } from "@/components/ProfileMenu";

function LogoutAll() {
  const logout = useLogout();
  const [busy, setBusy] = useState(false);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6">
      <h2 className="text-lg font-semibold text-slate-900">Signed-in devices</h2>
      <p className="mt-1 text-sm text-slate-600">Ends every Crowlearn session on all phones, tablets and computers, including this one.</p>
      <Button variant="outline" className="mt-3 h-11" disabled={busy} onClick={async () => { setBusy(true); await logout({ all: true }); }} data-testid="logout-all-btn">Log out of all devices</Button>
    </div>
  );
}

function NewsToggle() {
  const api = useApi();
  const { me } = useMe();
  const [on, setOn] = useState(!!me.user.marketing_opt_in);
  const change = async (v) => {
    try { await api.put("/me/preferences", { marketing_opt_in: v }); setOn(v); toast.success(v ? "You'll get news and offers" : "You won't get news and offers"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6">
      <h2 className="text-lg font-semibold text-slate-900">Email preferences</h2>
      <label className="mt-3 flex items-center justify-between gap-4 text-sm text-slate-800">Email me news, course reminders and offers from Crowlearn Services<Switch checked={on} onCheckedChange={change} data-testid="marketing-opt-in" /></label>
      <p className="mt-2 text-sm text-slate-600">Account and security emails are always sent.</p>
    </div>
  );
}

export default function Account() {
  const api = useApi();
  const { signOut } = useClerk();
  const [busy, setBusy] = useState("");

  const download = async () => {
    setBusy("export");
    try {
      const { data } = await api.get("/me/export");
      const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
      const a = Object.assign(document.createElement("a"), { href: url, download: "crowlearn-my-data.json" });
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const remove = async () => {
    setBusy("delete");
    try {
      await api.delete("/me");
      toast.success("Your account and data were deleted");
      await signOut();
    } catch (e) {
      toast.error(errMsg(e));
      setBusy("");
    }
  };

  return (
    <div className="max-w-3xl">
      <PageHeader eyebrow="Account" title="Privacy & data" />
      <div className="space-y-4">
        <NewsToggle />
        <LogoutAll />
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h2 className="text-lg font-semibold text-slate-900">Download your data</h2>
          <p className="mt-1 text-sm text-slate-600">Get a copy of your profile, progress, notes and activity as a JSON file.</p>
          <Button onClick={download} disabled={busy === "export"} variant="outline" className="mt-4" data-testid="export-data-btn">
            {busy === "export" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />} Download my data
          </Button>
        </div>
        <div className="rounded-2xl border border-rose-200 bg-white p-6">
          <h2 className="text-lg font-semibold text-slate-900">Delete your account</h2>
          <p className="mt-1 text-sm text-slate-600">Permanently removes your account and all data linked to it. This cannot be undone.</p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" className="mt-4" data-testid="delete-account-btn"><Trash2 className="mr-2 h-4 w-4" /> Delete my account</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete your Crowlearn account?</AlertDialogTitle>
                <AlertDialogDescription>All your profile details, progress and notes will be erased.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="delete-account-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={remove} className="bg-rose-600 hover:bg-rose-700" data-testid="delete-account-confirm">
                  {busy === "delete" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Delete permanently
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
}
